import React, { useContext, useState, useEffect, useCallback } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';
import VisuallyHidden from '@wix/da-a11y/pkg/components/VisuallyHidden';
import PlainButtonOrLink from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink';
import SquareHeartFilledSmall from '@wix/da-ds/pkg/Icons/16x16/SquareHeartFilled';
import SquareHeartOutlinedSmall from '@wix/da-ds/pkg/Icons/16x16/SquareHeartOutlined';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import { BiDataHook, BiEventType } from '@wix/da-bi/pkg/constants';
import SimpleTooltip from '@wix/da-ds/pkg/tooltips/dsPreset/SimpleTooltip';
import CustomTooltip from '@wix/da-ds/pkg/tooltips/dsPreset/CustomTooltip';
import shortenNumber from '@wix/da-ds/pkg/utils/shortenNumber';
import { CommentContext } from '../../CommentContext';
import ReactionList from '../ReactionList';
import ReactionsDisplay from '../ReactionsDisplay';
import s from './LikeButton.scss';
const LikeButton = ({ className , disabled , onLike , onUnlike  })=>{
    var _comment_user;
    const { t  } = useTranslation();
    const [isHovered, setIsHovered] = useState(false);
    const { comment , isReduxReady  } = useContext(CommentContext);
    const comment_reactions = useFlag('comment_reactions');
    const [isSSR, setIsSSR] = useState(true);
    useEffect(()=>{
        setIsSSR(typeof window === 'undefined');
    }, []);
    // Disable comment reactions in SSR because the data comes from local storage
    const commentReactionsEnabled = comment_reactions && !isSSR;
    const onMouseEnter = ()=>setIsHovered(true);
    const onMouseLeave = ()=>setIsHovered(false);
    const onClickLike = useCallback(()=>{
        // don't allow liking your own comment
        if (comment.isAuthor) {
            return;
        }
        if (comment.isLiked) {
            onUnlike(comment);
            // Unlike provokes a re-render, which will show the tooltip again unless we set hovered state off.
            if (commentReactionsEnabled) {
                setIsHovered(false);
            }
        } else {
            onLike(comment);
        }
    }, [
        comment,
        onLike,
        onUnlike,
        commentReactionsEnabled
    ]);
    const { isAuthor , isLiked , likes  } = comment;
    const canLike = !isAuthor;
    const hasLikes = likes > 0;
    const HeartIconSmall = isLiked ? SquareHeartFilledSmall : SquareHeartOutlinedSmall;
    const heartClasses = classnames(s['like'], isAuthor && s['not-clickable']);
    return(// Wrapper div because SimpleTooltip can render two elements at its root which will throw
    // off grid and flex layouts
    /*#__PURE__*/ React.createElement("div", {
        className: className,
        onMouseEnter: onMouseEnter,
        onMouseLeave: onMouseLeave
    }, /*#__PURE__*/ React.createElement(PlainButtonOrLink, {
        className: s['likebutton'],
        onClick: onClickLike,
        disabled: disabled !== null && disabled !== void 0 ? disabled : !isReduxReady,
        "data-hook": BiDataHook.COMMENT_LIKE_BUTTON,
        biData: canLike ? {
            evid: BiEventType.CLICK_COMMENT_LIKE,
            commenterid: (_comment_user = comment.user) === null || _comment_user === void 0 ? void 0 : _comment_user.useridUuid,
            commentid: comment.commentId,
            typeid: comment.typeId,
            itemid: comment.itemId,
            is_liking: isLiked ? undefined : 1
        } : undefined,
        "aria-label": t('comment.a11y.action.like')
    }, commentReactionsEnabled && (canLike && !isLiked ? /*#__PURE__*/ React.createElement(CustomTooltip, {
        forceVisible: isHovered,
        offsetModifier: [
            0,
            2
        ],
        placement: "top",
        secondaryPlacement: "start",
        usePortal: true,
        content: /*#__PURE__*/ React.createElement(ReactionList, {
            comment: comment
        })
    }, hasLikes ? /*#__PURE__*/ React.createElement(ReactionsDisplay, {
        className: s['reactions'],
        comment: comment
    }) : /*#__PURE__*/ React.createElement(HeartIconSmall, {
        className: heartClasses,
        size: IconSize.SMALLER,
        disableDefaultColors: true
    })) : /*#__PURE__*/ React.createElement(ReactionsDisplay, {
        className: s['reactions'],
        comment: comment
    })), !commentReactionsEnabled && /*#__PURE__*/ React.createElement(SimpleTooltip, {
        forceVisible: isHovered,
        offsetModifier: [
            0,
            2
        ],
        usePortal: true,
        text: isLiked ? t('comment.action.like.tooltip.liked') : t('comment.action.like.tooltip.notLiked')
    }, /*#__PURE__*/ React.createElement(HeartIconSmall, {
        className: heartClasses,
        size: IconSize.SMALLER,
        disableDefaultColors: true
    })), !commentReactionsEnabled && hasLikes && /*#__PURE__*/ React.createElement("span", {
        className: s['likes']
    }, shortenNumber(likes), /*#__PURE__*/ React.createElement(VisuallyHidden, null, t('comment.action.likes', {
        count: likes
    }))))));
};
LikeButton.displayName = 'LikeButton';
export default LikeButton;
